import DixaIcon from '@dixa/dixa-modern-icons';
import {
  Avatar,
  Badge,
  ButtonList,
  Collapsible,
  Divider,
  DropdownButton,
  FlexBox,
  IconButton,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ConversationsChannels } from 'lib-shared-legacy-types';
import type {
  ConversationMessageDeliveryEventComputed,
  User,
} from 'lib-shared-types-api';
import { memo, useEffect, useRef, useState } from 'react';

import { channelToSanitizationProfile } from 'app/utils/htmlSanitizer';
import { ConversationMessageDeliveryStatus } from 'components/conversation';
import { ConversationTimestamp } from 'components/conversation/components/conversationTimestamp/conversationTimestamp';
import { SafeInnerHtmlSetter } from 'components/shared/util/SafeInnerHtmlSetter';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import styles from './message.module.scss';

export type MessageProps = {
  computedDeliveryEvent?: ConversationMessageDeliveryEventComputed;
  csid?: number;
  messageId?: string;
  /** Extra classnames */
  className?: string;
  /** Author info */
  author: User;
  /** The description presented after the author name. */
  authorDescription?: React.ReactNode;
  /** Is the message internal? */
  internal?: boolean;
  /** The message markup (will be dangerously set) */
  markup?: string;
  /** Display both the markup and the content. */
  showMarkupAndContent?: boolean;
  /** Timestamp */
  date?: string;
  /** Is this the newest message? */
  newest?: boolean;
  /** Indicates flag of the first received message. */
  first?: boolean;
  /** Hide avatar, name and timestamp */
  simple?: boolean;
  /** Loading */
  loading?: boolean;
  /** Did the message fail */
  failed?: boolean | string;
  /** The low level failure reason */
  failureReason?: string;
  /** Is the users own message? */
  own?: boolean;
  /** Is an agents message? */
  isAgent?: boolean;
  /** Custom content to show instead of markup */
  content?: React.ReactNode;
  /** Images */
  images?: React.ReactNode;
  /** Attachments */
  attachments?: Array<React.ReactNode>;
  /** Menu */
  menu?: React.ReactNode[];
  /** Name menu */
  nameMenu?: React.ReactNode;
  /** Channel (used for sanitisation) */
  channel?: ConversationsChannels | 'notes' | null;
  onClick?: (e: React.MouseEvent) => void;
  /** Whether the message is an auto reply. */
  isAutoReply?: boolean;
  /** The name of the template used in the auto reply. */
  renderedTemplateName?: string;
  /** Attributes of the conversation this message comes from. */
  conversationAttributes?: Record<string, string>;
  /** Additional buttons to be added between "Scroll to top" and "Menu" buttons. */
  gutterButtons?: React.ReactNode;
  /** Conversation */
  conversation?: any;
  /** Whether the message is anonymized or not */
  isAnonymized?: boolean;
};

export const Message = memo(function Message(props: MessageProps): JSX.Element {
  const {
    csid,
    computedDeliveryEvent,
    messageId,
    className,
    author,
    authorDescription,
    internal,
    markup,
    showMarkupAndContent,
    date,
    newest,
    simple,
    failed,
    loading,
    own,
    isAgent,
    content,
    images,
    attachments,
    menu,
    nameMenu,
    onClick,
    channel,
    isAutoReply,
    conversationAttributes,
    first,
    failureReason,
    renderedTemplateName,
    gutterButtons,
    conversation,
    isAnonymized,
    ...otherProps
  } = props;
  const { conversationMessageDeliveryStatus } = useFeatureFlag([
    'conversation-message-delivery-status',
  ]);
  const measureRef = useRef<HTMLDivElement>(null);
  const gotoRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [stuck, setStuck] = useState(false);
  const [isAutoReplyContentOpen, setIsAutoReplyContentOpen] = useState(false);

  useEffect(() => {
    const measureElement = measureRef.current;
    const rootElement = rootRef.current;

    const intersectionObserver = new IntersectionObserver(
      ([measureElementEntry]) =>
        setStuck(measureElementEntry.intersectionRatio < 1),
      {
        root: rootElement?.closest('.messages'),
        threshold: [0, 1],
      },
    );

    if (measureElement) {
      intersectionObserver.observe(measureElement);
    }

    return () => intersectionObserver.disconnect();
  }, []);

  const handleMenuToggling = (isOpen: boolean) => {
    setIsMenuOpen(isOpen);
  };

  const goToTop = () => {
    if (gotoRef.current) {
      gotoRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleAutoReplyContent = (): void => {
    setIsAutoReplyContentOpen(
      (isAutoReplyContentOpen) => !isAutoReplyContentOpen,
    );
  };

  const renderMarkupAndContent = (): JSX.Element => {
    const { content, markup, showMarkupAndContent, channel } = props;

    const sanitizationProfile = channel
      ? channelToSanitizationProfile[channel]
      : 'default';

    return (
      <>
        {markup && (!content || showMarkupAndContent) && (
          <SafeInnerHtmlSetter
            element="div"
            content={markup}
            profile={sanitizationProfile}
            className={styles.text}
            dir="auto"
          />
        )}

        {content}
      </>
    );
  };

  const renderCollapsibleMarkupAndContent = (): JSX.Element => {
    const { renderedTemplateName } = props;

    return (
      <Collapsible
        isOpen={isAutoReplyContentOpen}
        header={
          <FlexBox alignItems="center" gap="100">
            <Text.Paragraph>{renderedTemplateName}</Text.Paragraph>
            <IconButton
              icon={isAutoReplyContentOpen ? 'angle-up' : 'angle-down'}
              variant="tertiary"
              onClick={toggleAutoReplyContent}
            />
          </FlexBox>
        }
      >
        <Divider className={styles.autoReplyDivider} />
        {renderMarkupAndContent()}
      </Collapsible>
    );
  };

  const rootClass = classnames(
    {
      [styles.message]: true,
      [styles.messageInternal]: !!internal,
      [styles.messageSimple]: !!simple,
      [styles.messageFailed]: !!failed,
      [styles.messageLoading]: !!loading,
      [styles.messageOwn]: !!own,
      [styles.messageAgent]: isAgent,
      [styles.messageActive]: isMenuOpen,
      [styles.messageStuck]: stuck,
    },
    className,
  );

  const showDotDotDot = menu && menu?.length > 0;
  const showGoToTop = showDotDotDot;

  return (
    <div {...otherProps} className={rootClass} ref={rootRef} onClick={onClick}>
      {showGoToTop && (
        <>
          <div className={styles.measure} ref={measureRef} />
          <div className={styles.goto} ref={gotoRef} />
        </>
      )}

      {!simple && (
        <div className={styles.avatarWrap}>
          <Avatar user={author} />
        </div>
      )}

      <div className={styles.messageWrap}>
        {!simple && (
          <FlexBox className={styles.name} alignItems="center" gap="200">
            <span>{author.name}</span>
            {authorDescription}
            {isAutoReply && (
              <Badge
                className={styles.autoReply}
                size="small"
                variant="primary"
              >
                Auto response
              </Badge>
            )}
            {internal && <> (Internal Note)</>}
            {!!nameMenu && <div>{nameMenu}</div>}
          </FlexBox>
        )}

        {isAutoReply
          ? renderCollapsibleMarkupAndContent()
          : renderMarkupAndContent()}

        {images}

        {!!attachments && attachments.length > 0 && (
          <div className={styles.attachments}>
            {attachments.map((attachment, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className={styles.attachment}>
                {attachment}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className={styles.gutter}>
          <div className={styles.status}>
            {!conversationMessageDeliveryStatus && failed && (
              <span className={styles.error}>
                <Tooltip
                  content={failed}
                  disabled={typeof failed !== 'string'}
                  wrap="span"
                  dark
                >
                  <DixaIcon
                    className="icon-vertical-fix"
                    icon="exclamation-circle"
                  />
                </Tooltip>
                &nbsp;Sending failed
              </span>
            )}

            <FlexBox gap="200" alignItems="center">
              {!simple && !!date && (
                <ConversationTimestamp
                  time={date}
                  relative={newest}
                  shouldDateFormatPersist={!newest}
                  textProps={{ className: styles.timestamp }}
                />
              )}

              {conversationMessageDeliveryStatus &&
                csid &&
                computedDeliveryEvent &&
                messageId &&
                !isAnonymized && (
                  <ConversationMessageDeliveryStatus
                    csid={csid}
                    computedDeliveryEvent={computedDeliveryEvent}
                    messageId={messageId}
                  />
                )}
            </FlexBox>
          </div>
          <FlexBox gap="200" alignItems="center">
            {showGoToTop && (
              <IconButton
                variant="secondary"
                icon="arrow-up"
                onClick={goToTop}
                className={styles.stuckButton}
              />
            )}
            {gutterButtons}
            {showDotDotDot && (
              <DropdownButton
                noArrow
                icon="dots-h"
                dropdown={<ButtonList>{menu}</ButtonList>}
                onToggle={handleMenuToggling}
              />
            )}
          </FlexBox>
        </div>
      </div>
    </div>
  );
});
